import React, { useCallback } from "react";
import PropTypes from "prop-types";
import RecordProcessPanel from "@viz-ui/components/RecordProcessPanel/RecordProcessPanel";
import { useProcessPanelProps } from "@viz-ui/components/DataTablePanel/hooks/useProcessPanelProps";
import processPanelService from "@viz-ui/services/processPanel/processPanelService";
import "./ProcessPanel.scss";

const ProcessPanel = ({
  appConfig = {},
  dataModel = {},
  checkedRowCount = 0,
  checkedRowIds = [],
  fieldFormatIdentifier = null,
  fieldModels = [],
  isFirstRecord = false,
  isLastRecord = false,
  isOpen = false,
  rowCheckedInverse = false,
  selectedRowData = null,
  selectedRowId = null,
  localize = null,
  onAddComment = () => {},
  onClosePanel = () => {},
  onCheck = () => {},
  onDeleteBulk = () => {},
  onDeleteRecord = () => {},
  onDeleteInProgress = () => {},
  onEnterBulkReviewMode = () => {},
  onExitBulkReviewMode = () => {},
  onNextRowClick = () => {},
  onPrevRowClick = () => {},
  onProcessBulk = () => {},
  onProcessRecord = () => {},
}) => {
  const processPanelProps = useProcessPanelProps({
    selectedRecordData: selectedRowData,
    selectedRecordId: selectedRowId,
    checkedRecordIds: checkedRowIds,
    checkedRecordCount: checkedRowCount,
    fieldModels,
    fieldFormatIdentifier,
    rowCheckedInverse,
    isFirstRecord,
    isLastRecord,
    isOpen,
    appConfig,
    dataModel,
    processPanelService,
    onProcessRecord,
    onDeleteRecord,
    onAddComment,
    onCheck,
    onClosePanel,
    onDeleteBulk,
    onDeleteInProgress,
    onEnterBulkReviewMode,
    onExitBulkReviewMode,
    onNextRowClick,
    onPrevRowClick,
    onProcessBulk,
  });

  const handleCloseClick = useCallback(() => {
    // Focus management logic from original controller
    if (checkedRowCount !== (checkedRowIds ? checkedRowIds.length : 0)) {
      const selectAll = document.querySelector(".data-grid__select-all");
      if (selectAll) selectAll.focus();
    } else {
      const rowId = selectedRowData ? selectedRowData["metadata.position"] : null;
      const rows = document.querySelectorAll(".htCenter");
      const checkBoxElements = document.querySelectorAll(".htCheckboxRendererInput");

      if (rows.length && checkBoxElements.length) {
        let rowIndex = 0;
        rows.forEach((row, index) => {
          if (row.innerHTML === `${rowId}`) {
            rowIndex = index;
          }
        });
        if (checkBoxElements[rowIndex]) checkBoxElements[rowIndex].focus();
      }
    }
    onClosePanel();
  }, [checkedRowCount, checkedRowIds, selectedRowData, onClosePanel]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="process-panel__main-container"
      role="dialog"
      aria-labelledby="process-panel-title"
    >
      <div className="viz-panel__header">
        <h2 tabIndex="-1" id="process-panel-title">
          {localize
            ? localize.getLocalizedString(
                (processPanelProps && processPanelProps.headlineLabelLocalizeKey) || "_ProcessPanel.Headline.Label_"
              )
            : "Process Records"}
        </h2>
        <button
          type="button"
          className="viz-panel__header-button"
          id="process-panel-close-button"
          onClick={handleCloseClick}
          aria-label={localize ? localize.getLocalizedString("_General.Close.Label_") : "Close"}
        >
          <i className="acl-i-times" aria-hidden="true" />
          {localize ? localize.getLocalizedString("_General.Close.Label_") : "Close"}
        </button>
      </div>

      <div className="process-panel__body">{processPanelProps && <RecordProcessPanel {...processPanelProps} />}</div>
    </div>
  );
};

ProcessPanel.propTypes = {
  appConfig: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  dataModel: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  checkedRowCount: PropTypes.number,
  checkedRowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fieldFormatIdentifier: PropTypes.string,
  fieldModels: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
  isFirstRecord: PropTypes.bool,
  isLastRecord: PropTypes.bool,
  isOpen: PropTypes.bool,
  rowCheckedInverse: PropTypes.bool,
  selectedRowData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  selectedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  localize: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onAddComment: PropTypes.func,
  onClosePanel: PropTypes.func,
  onCheck: PropTypes.func,
  onDeleteBulk: PropTypes.func,
  onDeleteRecord: PropTypes.func,
  onDeleteInProgress: PropTypes.func,
  onEnterBulkReviewMode: PropTypes.func,
  onExitBulkReviewMode: PropTypes.func,
  onNextRowClick: PropTypes.func,
  onPrevRowClick: PropTypes.func,
  onProcessBulk: PropTypes.func,
  onProcessRecord: PropTypes.func,
};

ProcessPanel.defaultProps = {
  appConfig: {},
  dataModel: {},
  checkedRowCount: 0,
  checkedRowIds: [],
  fieldFormatIdentifier: null,
  fieldModels: [],
  isFirstRecord: false,
  isLastRecord: false,
  isOpen: false,
  rowCheckedInverse: false,
  selectedRowData: null,
  selectedRowId: null,
  localize: null,
  onAddComment: () => {},
  onClosePanel: () => {},
  onCheck: () => {},
  onDeleteBulk: () => {},
  onDeleteRecord: () => {},
  onDeleteInProgress: () => {},
  onEnterBulkReviewMode: () => {},
  onExitBulkReviewMode: () => {},
  onNextRowClick: () => {},
  onPrevRowClick: () => {},
  onProcessBulk: () => {},
  onProcessRecord: () => {},
};

export default ProcessPanel;
